import React from 'react';
import LegalPage from '@/components/LegalPage';

const CONTENT = `Esta política explica qué datos junta EVALPRO, para qué, con quién se comparten y qué podés hacer con ellos. Está pensada según la **Ley 25.326 de Protección de los Datos Personales** y su normativa. EVALPRO trata datos de salud, que la ley considera **datos sensibles**, y por eso los cuida con reglas más estrictas.

## 1. Quién es responsable

EVALPRO es un servicio de ejerciciofisicoysalud.com (en adelante, "EVALPRO", "nosotros"). Para cualquier tema de datos personales: **ejerciciofisicoysaludarg@gmail.com**

Hay que distinguir dos casos:

- **Datos de quien tiene la cuenta** (el profesional o el gimnasio): EVALPRO es el responsable.
- **Datos de los alumnos o pacientes**: el responsable es el profesional que los carga o que manda el link. EVALPRO los trata **por cuenta y orden de ese profesional**, solo para prestarle el servicio, como encargado del tratamiento (art. 25 de la Ley 25.326). No los usa para nada más.

## 2. Qué datos juntamos

**a) De quien tiene la cuenta**
- Nombre, mail y, si lo das, teléfono.
- Contraseña: se guarda cifrada, de forma que ni nosotros podemos leerla.
- Datos de la compra: producto, monto, fecha, medio de pago e identificador del pago. **No vemos ni guardamos el número de tu tarjeta**: eso lo procesa Mercado Pago.
- Datos técnicos: dirección IP, navegador, intentos de ingreso y uso de la plataforma.

**b) De los alumnos o pacientes**
- Datos identificatorios (nombre, edad, sexo).
- Datos antropométricos (peso, altura, cintura).
- Datos de salud: presión arterial, frecuencia cardíaca, enfermedades, síntomas, factores de riesgo, medicación, estudios, limitaciones osteoarticulares.
- Actividad física, pruebas funcionales y reentrevistas.

**c) De quien prueba la demo o pide la guía gratuita**
- Solo el mail. **Las respuestas de la demo no se guardan.**

**d) De quien llega desde un anuncio**
- Las cookies de Meta y los datos de la campaña que trae el enlace (por ejemplo, de qué anuncio vino). Ver el punto 7.

## 3. Cómo llegan los datos del alumno

Hay dos caminos:

1. **Los carga el profesional.** Antes de empezar, declara que tiene el consentimiento informado del alumno o paciente.
2. **Los carga el propio alumno**, con un link que le manda su profesional. El link es personal, vence a los 7 días y sirve una sola vez. Antes de enviar, el alumno acepta expresamente que sus datos se compartan con su profesional.

Si el alumno es **menor de 18 años**, el consentimiento lo da su madre, padre o tutor, y el formulario lo tiene que completar con un adulto.

## 4. Para qué los usamos

- Que la plataforma funcione: generar la entrevista, el informe, el seguimiento y las pruebas funcionales.
- Gestionar tu cuenta, tus compras y tu acceso.
- Mandarte los mails del servicio (código de ingreso, compra, recuperación de contraseña).
- Mandarte información sobre EVALPRO si dejaste tu mail en la demo o en la guía. **Cada mail trae un link para darte de baja con un clic.**
- Medir qué anuncios funcionan (punto 7).
- Darte soporte.
- Cumplir obligaciones legales, contables y fiscales.
- Mejorar el servicio con información **anónima y agregada**, sin identificar a nadie.

Los datos de salud de los alumnos **no se usan para publicidad ni se mandan a Meta ni a ninguna plataforma de anuncios**.

## 5. Base legal

- Para los datos de tu cuenta: tu consentimiento al registrarte y aceptar estos textos, y la prestación del servicio que contrataste.
- Para los datos de los alumnos: el consentimiento informado del titular, que obtiene el profesional (o que el propio alumno da en el link). Contar con ese consentimiento es responsabilidad del profesional.

## 6. Con quién se comparten

**No vendemos ni cedemos datos a nadie.** Solo trabajan con ellos los proveedores que hacen falta para que EVALPRO funcione, cada uno para su tarea:

- **Render**: servidor donde corre la aplicación.
- **Netlify**: aloja el sitio web.
- **MongoDB Atlas**: base de datos.
- **Mercado Pago**: procesa los pagos.
- **Resend**: envía los mails.
- **Meta (Facebook / Instagram)**: medición de anuncios (punto 7).
- **Google (Gmail)**: cuando nos escribís para soporte.

También podemos tener que entregar datos si lo pide un juez o una autoridad competente por la vía formal.

## 7. Meta, cookies y almacenamiento en tu navegador

**Meta.** El sitio usa el Píxel de Meta y su API de Conversiones para saber si una visita o una compra vino de un anuncio. Lo que se manda a Meta es: las páginas que visitás del sitio, el inicio de compra y la compra con su monto, tu IP, tu navegador, las cookies de Meta y, al comprar, tu mail y tu teléfono **cifrados con un sistema irreversible (hash)**. **Nunca se manda nada de la entrevista ni de salud.** Podés limitar esto desde la configuración de anuncios de tu cuenta de Facebook o Instagram, o bloqueando las cookies de terceros en tu navegador.

**Almacenamiento local.** La plataforma guarda en tu navegador tu sesión (para que no tengas que ingresar cada vez), de qué anuncio llegaste y el vencimiento de la promoción. Si borrás los datos del navegador, se borra todo eso y tenés que volver a ingresar.

## 8. Datos fuera de Argentina

Los proveedores del punto 6 tienen servidores fuera de la Argentina (por ejemplo, en Estados Unidos). Al aceptar esta política das tu consentimiento expreso para esa transferencia, que se hace con proveedores que aplican estándares reconocidos de seguridad.

## 9. Seguridad

La conexión va siempre cifrada (HTTPS), las contraseñas se guardan cifradas, el acceso a la base está restringido y cada profesional ve solo sus propios alumnos. Ningún sistema es invulnerable: si hubiera un incidente que afecte tus datos, te avisamos y tomamos las medidas que correspondan.

## 10. Cuánto tiempo se guardan

- **Datos de tu cuenta y de tus alumnos**: mientras la cuenta esté activa.
- **Si pedís la baja de la cuenta**: se borra todo en un plazo máximo de **30 días corridos**, salvo lo que haya que conservar por ley (por ejemplo, los datos de facturación, durante los plazos fiscales).
- **Si pedís borrar un alumno**: se borran sus datos en el mismo plazo.
- **Links del alumno vencidos**: se borran solos.
- **Mail de la demo o de la guía**: hasta que te des de baja.

## 11. Tus derechos

Podés pedir en cualquier momento:

- **Acceso**: saber qué datos tuyos tenemos. Te respondemos dentro de los **10 días corridos**.
- **Rectificación**: corregir datos equivocados o incompletos.
- **Supresión**: que se borren.
- **Actualización**: que se completen o se pongan al día.

Para rectificación, actualización o supresión respondemos dentro de los **5 días hábiles**. Escribí a **ejerciciofisicoysaludarg@gmail.com** diciendo qué pedís, desde el mail de tu cuenta o con algo que acredite que sos vos.

**Si sos alumno o paciente**, podés pedírselo a tu profesional o escribirnos directamente: lo resolvemos junto con él.

El titular de los datos personales tiene la facultad de ejercer el derecho de acceso a los mismos en forma gratuita a intervalos no inferiores a seis meses, salvo que se acredite un interés legítimo al efecto conforme lo establecido en el artículo 14, inciso 3 de la Ley Nº 25.326.

## 12. Autoridad de control

La AGENCIA DE ACCESO A LA INFORMACIÓN PÚBLICA, en su carácter de Órgano de Control de la Ley N° 25.326, tiene la atribución de atender las denuncias y reclamos que interpongan quienes resulten afectados en sus derechos por incumplimiento de las normas vigentes en materia de protección de datos personales.

## 13. Cambios en esta política

Si la cambiamos, publicamos la versión nueva acá con su fecha. Si el cambio es importante, también te avisamos por mail.

## 14. Contacto

Consultas, pedidos o reclamos sobre tus datos: **ejerciciofisicoysaludarg@gmail.com**
`;

export default function PrivacyPage() {
  return (
    <LegalPage
      title="Política de Privacidad — EVALPRO"
      lastUpdated="Última actualización: 31 de octubre de 2026"
      content={CONTENT}
      testId="privacy-page"
    />
  );
}
